'use strict';

import React, { useRef } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import { connect } from 'react-redux';
import Hashids from 'hashids';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import cn from 'classnames/bind';

import { appSelectors } from '@/state/domains/app';
import { ordersSelectors, ordersOperations } from '@/state/domains/orders';
import { usersOperations, usersSelectors } from '@/state/domains/users';
import { locationsSelectors, locationsOperations } from '@/state/domains/locations';
import { attemptsSelectors, attemptsOperations } from '@/state/domains/attempts';

import { flagColors, priorityColors } from '@/state/utilities';
import { getHistory } from '@/history';

import ItemTypes from '../../../ItemTypes';
import Link from '@/views/common/Link';

import s from './styles.scss';

const sx = cn.bind(s);

export default connect(
	(state, props) => ({
		location: locationsSelectors.getLocation(state, props),
		job: ordersSelectors.getJob(state, props),
		jobs: ordersSelectors.getPropJobs(state, props),
	}),
	{
	}
)(
	({
		user,
		date,
		listItem,
		job,
		jobs,
		index,
		location,
		locationId,
		locationList,
		attempts,
		urlParams,
		isDayAttempted,
		connectDragSource,
		connectDropTarget,
		connectDragPreview,
		isSelected,
		toggleSelected,
		postFlagIncrement,
		postPriorityIncrement,
		postReorderedLocations,
		isMobile,
		moveOrder,
		canEdit,
		expanded,
	}) => {

		const ref = useRef(null);

		const [ { handlerId }, drop ] = useDrop({
			accept: ItemTypes.ORDER,
			collect(monitor) {
				return {
					handlerId: monitor.getHandlerId(),
				}
			},
			hover(item, monitor) {

				if (!ref.current) {
					return;
				}

				const dragIndex = item.index;
				const hoverIndex = index;

				// Don't replace items with themselves
				if (dragIndex === hoverIndex) {
					return;
				}

				// Determine rectangle on screen
				const hoverBoundingRect = ref.current?.getBoundingClientRect();

				// Get vertical middle
				const hoverMiddleY =
					(hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;

				// Determine mouse position
				const clientOffset = monitor.getClientOffset();

				// Get pixels to the top
				const hoverClientY = clientOffset.y - hoverBoundingRect.top;

				// Only perform the move when the mouse has crossed half of the items height
				// When dragging downwards, only move when the cursor is below 50%
				// When dragging upwards, only move when the cursor is above 50%
				// Dragging downwards
				if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
					return;
				}
				// Dragging upwards
				if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
					return;
				}

				// Time to actually perform the action
				moveOrder(dragIndex, hoverIndex);

				// Note: we're mutating the monitor item here!
				// Generally it's better to avoid mutations,
				// but it's good here for the sake of performance
				// to avoid expensive index searches.
				item.index = hoverIndex;
			},
		});

		const [ { isDragging }, drag, preview ] = useDrag({
			type: ItemTypes.ORDER,
			item: () => {
				return { id: listItem.locationId, index }
			},
			end: (item, monitor) => {

				const locations = locationList.map(i => ({
					locationId: i.locationId,
					priority: i.priority,
					flag: i.flag,
					jobs: i.jobs,
				}));

				postReorderedLocations(
					user.username,
					date,
					locations
				);
			},
			collect: (monitor) => ({
				isDragging: monitor.isDragging(),
			}),
		});

		drag(drop(ref));

		//const { id, property, subunit, served, badAddress } = job;
		const hashids = new Hashids();
		let id = hashids.encodeHex(locationId);

		let { subunit, streetNumber, streetName, city, state, zip } = location;

		streetNumber = streetNumber || '';
		streetName = streetName || '';
		subunit = subunit || '';

		let txt = (`${ streetNumber } ${ streetName }`).trim();

		if (!txt) {
			txt = `${ city }, ${ state } ${ zip }`;
		}

		let classNames = [ 'listItem' ];

		/*if (served) {
			classNames.push('isServed');
		} else if (badAddress) {
			classNames.push('isBadAddress');
		}*/

		const history = getHistory();

		const { username } = urlParams;

		let url;

		if (job) {
			const { companyId, id: jobId } = job;
			url = `/user/${ username }/${ date }/${ companyId }/${ jobId }/${ id }`;
		} else {
			url = `/user/${ username }/${ date }/location/${ id }`;
		}

		const items = jobs.reduce(
			(items, job) => {

				const loc = job.locations.find(l => l.locationId === location._id);

				const isAttempted = attempts.find(a => (
					a.jobId === job._id &&
					a.locationId === location._id
				));

				if (loc) {

					const recipient = loc.recipient || '"Jane/John Doe"';

					return [
						...items,
						{
							recipient,
							task: loc.task,
							isAttempted,
							completed: loc.completed,
							invalid: loc.invalid,
							job,
						},
					];
				}
				return items;
			},
			[]
		);

		const someAttempted = items.find(i => i.isAttempted);
		const allAttempted = items.reduce(
			(isAttempted, item) => {
				if (isAttempted) {
					return item.isAttempted;
				}
				return isAttempted;
			},
			true
		);

		if (allAttempted) {
			//txt = <s className={ s.txt }>{ txt }</s>;
		}
		//<div ref={ preview } className={ s.rowCont } data-handler-id={ handlerId } >

		const taskIcons = {
			SERVICE: 'scale-balanced',
			PICKUP: 'hand-holding',
			DELIVERY: 'truck',
		};

		return (
			<tbody ref={ preview } className={ s.rowCont } data-handler-id={ handlerId } >
				<tr className={ s.row }>
					<td className={ sx('cell', 'attemptCheck') }>
						{ someAttempted &&
							<FontAwesomeIcon
								icon='check'
								className={
									sx(
										'someAttemptedCheck',
										{
											allAttemptedCheck: allAttempted,
											served: (
												items.length === 1 &&
												items[ 0 ].completed &&
												!items[ 0 ].invalid
											),
											badAddress: (
												items.length === 1 &&
												items[ 0 ].completed &&
												items[ 0 ].invalid
											),
										}
									)
								}
								color='#555'
							/>
						}
					</td>
					{
						canEdit &&
						<td
							className={ sx('cell', 'check') }
							onClick={ () => toggleSelected(index) }
						>
							<input type='checkbox' checked={ isSelected } onChange={ () => {} }/>
						</td>
					}
					<td className={ sx('cell', 'num') }>
						{ `${ index + 1 }.` }
					</td>
					<td className={ sx('cell', 'address', { padLeft: !canEdit }) }>
						<Link
							to={ url }
							className={ s.jobLink }
						>
							<span className={ s.txt }>{ txt }</span>
						</Link>
					</td>
					{
						canEdit &&
						<>
							<td className={ sx('cell', 'flag') } onClick={ () => postPriorityIncrement(index) }>
								<FontAwesomeIcon icon='exclamation' color={ priorityColors[ listItem.priority ] } />
							</td>
							<td className={ sx('cell', 'flag') } onClick={ () => postFlagIncrement(index) }>
								<FontAwesomeIcon icon='flag' color={ flagColors[ listItem.flag ] } />
							</td>
							<td
								className={ sx('cell', 'pinpoint') }
								onClick={ () => history.push(
									`/user/${ username }/${ date }/route`,
									{
										pinpoint: location._id,
										mapView: true,
										listView: !isMobile,
									},
								)}
							>
								<FontAwesomeIcon icon='map-marker-alt' color='#bbb' />
							</td>
							<td ref={ ref } className={ sx('cell', 'drag') }>
								<FontAwesomeIcon icon='grip-vertical' color='#bbb' />
							</td>
						</>
					}
					<td className={ s.placeholder } />
				</tr>
					
				{
					expanded && items.map((item, i) => (
						<tr className={ s.infoRow } key={ i }>
							<td className={ s.placeholder }></td>
							<td className={ s.placeholder }></td>
							{ canEdit && <td className={ sx('placeholder', 'icon') }>
								{
									item.completed ? 
										(
											item.invalid ?
												<FontAwesomeIcon icon='xmark' className={ s.badAddress } /> :
												<FontAwesomeIcon icon='check' className={ s.served } />
										):
										(
											item.isAttempted ?
												<FontAwesomeIcon icon='check' color='#555' />:
												<s />
										)
								}
							</td> }
							<th className={ s.recipient } key={ i } title={ item.recipient }>
								{ item.recipient }
							</th>
							{
								canEdit && (
									<>
										<td className={ s.placeholder }></td>
										<td className={ s.placeholder }></td>
										<td className={ s.placeholder }></td>
										<td className={ s.placeholder }></td>
									</>
								)
							}
							<td className={ s.placeholder } />
						</tr>
					))
				}
			</tbody>
		);
	}
);
